import React, { useMemo } from 'react'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  DatePosition,
  getClampedPercentXForDate,
  getPercentXForDate,
} from '../hooks/useDatePositions'
import { GanttColors, GanttDimensions } from '../constants'
import { Dependency } from './DependencyContext'
import { FallbackLinkInfo } from '../utils/visibilityFilters'

interface DependencyLinesProps {
  datePositions: DatePosition[]
  dependencies: Dependency[]
  partialDependencies: FallbackLinkInfo<Dependency>[]
  currentRowIndex: number
  scheduledStart?: CalendarDateVO
  scheduledEnd?: CalendarDateVO
  today?: CalendarDateVO
  onLineClick?: (dep: Dependency, event: React.MouseEvent) => void
  onPartialLineClick?: (
    info: FallbackLinkInfo<Dependency>,
    event: React.MouseEvent
  ) => void
  containerWidth: number
}

/**
 * DependencyLines - Renders dependency line segments for this row
 *
 * Each row draws its portion of dependency lines:
 * - Source row: Line from bar end to row edge (going down or up to target)
 * - Intermediate row: Vertical line through row
 * - Target row: Line from row edge to bar start with arrow
 */
export const DependencyLines = React.memo<DependencyLinesProps>(
  ({
    datePositions,
    dependencies,
    partialDependencies,
    currentRowIndex,
    scheduledStart,
    scheduledEnd,
    today,
    onLineClick,
    onPartialLineClick,
    containerWidth,
  }) => {
    // Build elements and collect used colors in a single pass
    const { elements, markerDefs } = useMemo(() => {
      const result: React.ReactNode[] = []
      const usedColors = new Set<string>()

      dependencies.forEach((dep, depIndex) => {
        const handleClick = onLineClick
          ? (e: React.MouseEvent) => onLineClick(dep, e)
          : undefined
        const lineColor = dep.color || GanttColors.DEPENDENCY_LINE
        usedColors.add(lineColor)
        const markerId = `arrow-${currentRowIndex}-${lineColor.replace('#', '')}`
        const depElements = generateDependencyElements(
          dep,
          depIndex,
          datePositions,
          currentRowIndex,
          scheduledStart,
          scheduledEnd,
          markerId,
          handleClick,
          lineColor,
          containerWidth,
          undefined,
          undefined,
          today
        )
        result.push(...depElements)
      })

      // Partial dependencies: draw lines up to terminus row with dashed ending
      partialDependencies.forEach((info, idx) => {
        const { link, from, to } = info
        const handleClick = onPartialLineClick
          ? (e: React.MouseEvent) => onPartialLineClick(info, e)
          : undefined
        const lineColor = link.color || GanttColors.DEPENDENCY_LINE
        usedColors.add(lineColor)

        // Determine direction based on resolved row positions.
        const isFromAbove = from.rowIndex <= to.rowIndex

        // Ensure terminus is in the correct direction from the visible endpoint.
        // Only adjusts when ancestor resolves to the same row or crosses over.
        let effectiveFromRow = from.rowIndex
        let effectiveToRow = to.rowIndex
        if (isFromAbove && effectiveToRow <= effectiveFromRow) {
          effectiveToRow = effectiveFromRow + 1
        } else if (!isFromAbove && effectiveFromRow <= effectiveToRow) {
          effectiveFromRow = effectiveToRow + 1
        }
        const terminusRowIndex = from.isFallback
          ? effectiveFromRow
          : effectiveToRow

        const depElements = generateDependencyElements(
          {
            ...link,
            fromRowIndex: effectiveFromRow,
            toRowIndex: effectiveToRow,
          },
          dependencies.length + idx,
          datePositions,
          currentRowIndex,
          scheduledStart,
          scheduledEnd,
          undefined, // no arrow marker for partial lines
          handleClick,
          lineColor,
          containerWidth,
          terminusRowIndex,
          isFromAbove,
          today
        )
        result.push(...depElements)
      })

      // Build marker definitions for each unique color
      const markers = Array.from(usedColors).map(color => (
        <marker
          key={color}
          id={`arrow-${currentRowIndex}-${color.replace('#', '')}`}
          viewBox="0 0 10 10"
          refX="6"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={color} />
        </marker>
      ))

      return { elements: result, markerDefs: markers }
    }, [
      datePositions,
      dependencies,
      partialDependencies,
      currentRowIndex,
      scheduledStart,
      scheduledEnd,
      today,
      onLineClick,
      onPartialLineClick,
      containerWidth,
    ])

    if (currentRowIndex < 0) return null

    return (
      <g className="dependency-lines" strokeLinecap="round">
        <defs>{markerDefs}</defs>
        {elements}
      </g>
    )
  }
)

DependencyLines.displayName = 'DependencyLines'

// ============================================================================
// Helper Types
// ============================================================================

interface ResolvedDates {
  fromEndDate: CalendarDateVO | null
  toStartDate: CalendarDateVO | null
}

type RowRole = 'source' | 'intermediate' | 'target'

// ============================================================================
// Element Generation
// ============================================================================

function generateDependencyElements(
  dependency: Dependency,
  depIndex: number,
  datePositions: DatePosition[],
  currentRowIndex: number,
  scheduledStart?: CalendarDateVO,
  scheduledEnd?: CalendarDateVO,
  markerId?: string,
  onClick?: (e: React.MouseEvent) => void,
  lineColor: string = GanttColors.DEPENDENCY_LINE,
  containerWidth: number = 0,
  terminusRowIndex?: number,
  isFromAboveOverride?: boolean,
  today?: CalendarDateVO
): React.ReactNode[] {
  const { fromRowIndex, toRowIndex } = dependency
  const elements: React.ReactNode[] = []

  // Check if this row is involved in this dependency
  const minRow = Math.min(fromRowIndex, toRowIndex)
  const maxRow = Math.max(fromRowIndex, toRowIndex)
  if (currentRowIndex < minRow || currentRowIndex > maxRow) return elements

  // Resolve dates from dependency object and current row's props
  const resolvedDates = resolveDependencyDates(
    dependency,
    currentRowIndex,
    scheduledStart,
    scheduledEnd,
    today
  )

  // For partial deps (terminus row exists), always use the link's embedded dates.
  // The "source" row in a partial dep is an ancestor (e.g. a collapsed parent),
  // whose own dates differ from the actual hidden endpoint's dates.
  const dates: ResolvedDates =
    terminusRowIndex !== undefined
      ? {
          fromEndDate: dependency.fromEndDate
            ? CalendarDateVO.of(dependency.fromEndDate)
            : resolvedDates.fromEndDate,
          toStartDate: dependency.toStartDate
            ? CalendarDateVO.of(dependency.toStartDate)
            : resolvedDates.toStartDate,
        }
      : resolvedDates

  // Skip when both dates are outside the visible range on the same side
  if (
    dates.fromEndDate &&
    dates.toStartDate &&
    getPercentXForDate(datePositions, dates.fromEndDate, 'right') === null &&
    getPercentXForDate(datePositions, dates.toStartDate, 'left') === null
  ) {
    const clampedFrom = getClampedPercentXForDate(
      datePositions,
      dates.fromEndDate,
      'right'
    )
    const clampedTo = getClampedPercentXForDate(
      datePositions,
      dates.toStartDate,
      'left'
    )
    if (
      clampedFrom === null ||
      clampedTo === null ||
      clampedFrom === clampedTo
    ) {
      return elements
    }
  }

  // Calculate positions (using percentage)
  const positions = calculatePositions(dates, datePositions, containerWidth)

  const isFromRowAboveToRow = isFromAboveOverride ?? fromRowIndex < toRowIndex
  const rowRole = getRowRole(currentRowIndex, fromRowIndex, toRowIndex)
  const isTerminus =
    terminusRowIndex !== undefined && currentRowIndex === terminusRowIndex
  const keyPrefix = `dep-${depIndex}`

  const { DEPENDENCY_LINE_WIDTH } = GanttDimensions
  const DASHED_PATTERN = '4 6'

  // Y positions as percentages
  // SVG doesn't support calc(), so we use percentage values only
  // Top: -10% (slightly above row), Bottom: 110% (slightly below row)
  // This extends the lines into adjacent rows
  const barCenterY = '50%'
  const topEdgeY = '-10%'
  const bottomEdgeY = '110%'

  // For detour around bar, offset from center
  const detourOffsetPercent = 30 // 30% from center = above/below bar

  // Terminus row: draw a dashed vertical stub instead of source/target.
  // Direction depends on whether the terminus is on the from or to side:
  // - from-side terminus (source hidden): dashed stub from center toward target
  // - to-side terminus (target hidden): dashed stub from source direction to center
  if (isTerminus) {
    if (positions.fromExitX !== null) {
      const isFromSideTerminus = currentRowIndex === fromRowIndex
      const dashedY1 = isFromSideTerminus
        ? barCenterY
        : isFromRowAboveToRow
          ? topEdgeY
          : bottomEdgeY
      const dashedY2 = isFromSideTerminus
        ? isFromRowAboveToRow
          ? bottomEdgeY
          : topEdgeY
        : barCenterY

      elements.push(
        <g
          key={`${keyPrefix}-terminus`}
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : undefined }}
        >
          <line
            x1={`${positions.fromExitX}%`}
            y1={dashedY1}
            x2={`${positions.fromExitX}%`}
            y2={dashedY2}
            stroke={lineColor}
            strokeWidth={DEPENDENCY_LINE_WIDTH}
            strokeDasharray={DASHED_PATTERN}
          />
          {/* Transparent hit area */}
          <line
            x1={`${positions.fromExitX}%`}
            y1={dashedY1}
            x2={`${positions.fromExitX}%`}
            y2={dashedY2}
            stroke="transparent"
            strokeWidth={12}
            style={{ pointerEvents: 'stroke' }}
          />
        </g>
      )
    }
    return elements
  }

  // Skip source/intermediate when fromDate is outside visible range (bar not visible)
  const isFromDateInRange = dates.fromEndDate
    ? getPercentXForDate(datePositions, dates.fromEndDate, 'right') !== null
    : false
  const isToDateInRange = dates.toStartDate
    ? getPercentXForDate(datePositions, dates.toStartDate, 'left') !== null
    : false

  switch (rowRole) {
    case 'source':
      if (
        isFromDateInRange &&
        positions.fromX !== null &&
        positions.fromExitX !== null
      ) {
        // Draw from bar end, horizontal to exit point, then vertical to edge
        elements.push(
          <g
            key={`${keyPrefix}-source`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* Horizontal line from bar end to exit point */}
            <line
              x1={`${positions.fromX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={barCenterY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Vertical line to row edge */}
            <line
              x1={`${positions.fromExitX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={isFromRowAboveToRow ? bottomEdgeY : topEdgeY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Transparent hit areas for easier right-click */}
            <line
              x1={`${positions.fromX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={barCenterY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={`${positions.fromExitX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={isFromRowAboveToRow ? bottomEdgeY : topEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break

    case 'intermediate':
      if (isFromDateInRange && positions.fromExitX !== null) {
        // Draw vertical line through entire row
        elements.push(
          <g
            key={`${keyPrefix}-intermediate`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            <line
              x1={`${positions.fromExitX}%`}
              y1={topEdgeY}
              x2={`${positions.fromExitX}%`}
              y2={bottomEdgeY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Transparent hit area */}
            <line
              x1={`${positions.fromExitX}%`}
              y1={topEdgeY}
              x2={`${positions.fromExitX}%`}
              y2={bottomEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break

    case 'target':
      if (positions.toX !== null) {
        const entryY = isFromRowAboveToRow ? topEdgeY : bottomEdgeY
        const detourY = isFromRowAboveToRow
          ? `${50 - detourOffsetPercent}%`
          : `${50 + detourOffsetPercent}%`

        if (positions.fromExitX !== null && positions.toEntryX !== null) {
          // Use detour path when:
          // 1. Dates are reversed (source end is after target start)
          // 2. Positions cross due to offsets (adjacent dates where fromExitX >= toX)
          // This ensures the arrow always arrives from the LEFT, pointing RIGHT toward the bar
          const needsDetour =
            isReversedDateOrder(dates) || positions.fromExitX >= positions.toX

          elements.push(
            <g
              key={`${keyPrefix}-target`}
              onClick={onClick}
              style={{ cursor: onClick ? 'pointer' : undefined }}
            >
              {needsDetour ? (
                // Detour path: vertical → horizontal detour → vertical → horizontal arrow
                // The final segment always goes left→right so the arrow points at the bar
                <>
                  {/* Vertical from edge to detour level (skip when fromDate is outside visible range) */}
                  {isFromDateInRange && (
                    <line
                      x1={`${positions.fromExitX}%`}
                      y1={entryY}
                      x2={`${positions.fromExitX}%`}
                      y2={detourY}
                      stroke={lineColor}
                      strokeWidth={DEPENDENCY_LINE_WIDTH}
                    />
                  )}
                  {/* Horizontal detour */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={detourY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                  />
                  {/* Vertical from detour to center (skip when toDate is outside visible range) */}
                  {isToDateInRange && (
                    <line
                      x1={`${positions.toEntryX}%`}
                      y1={detourY}
                      x2={`${positions.toEntryX}%`}
                      y2={barCenterY}
                      stroke={lineColor}
                      strokeWidth={DEPENDENCY_LINE_WIDTH}
                    />
                  )}
                  {/* Horizontal to bar start with arrow (skip when toDate is outside visible range) */}
                  {isToDateInRange && (
                    <line
                      x1={`${positions.toEntryX}%`}
                      y1={barCenterY}
                      x2={`${positions.toX}%`}
                      y2={barCenterY}
                      stroke={lineColor}
                      strokeWidth={DEPENDENCY_LINE_WIDTH}
                      strokeLinecap="butt"
                      markerEnd={markerId ? `url(#${markerId})` : undefined}
                    />
                  )}
                  {/* Transparent hit areas */}
                  {isFromDateInRange && (
                    <line
                      x1={`${positions.fromExitX}%`}
                      y1={entryY}
                      x2={`${positions.fromExitX}%`}
                      y2={detourY}
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ pointerEvents: 'stroke' }}
                    />
                  )}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={detourY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                  {isToDateInRange && (
                    <line
                      x1={`${positions.toEntryX}%`}
                      y1={detourY}
                      x2={`${positions.toEntryX}%`}
                      y2={barCenterY}
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ pointerEvents: 'stroke' }}
                    />
                  )}
                  {isToDateInRange && (
                    <line
                      x1={`${positions.toEntryX}%`}
                      y1={barCenterY}
                      x2={`${positions.toX}%`}
                      y2={barCenterY}
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ pointerEvents: 'stroke' }}
                    />
                  )}
                </>
              ) : (
                // Normal case: direct path (fromExitX is to the left of toX)
                // Arrow goes left→right, so marker correctly points at the bar
                <>
                  {/* Vertical from edge to center (skip when fromDate is outside visible range) */}
                  {isFromDateInRange && (
                    <line
                      x1={`${positions.fromExitX}%`}
                      y1={entryY}
                      x2={`${positions.fromExitX}%`}
                      y2={barCenterY}
                      stroke={lineColor}
                      strokeWidth={DEPENDENCY_LINE_WIDTH}
                    />
                  )}
                  {/* Horizontal to bar start with arrow (skip arrow when toDate is outside visible range) */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                    strokeLinecap="butt"
                    markerEnd={
                      isToDateInRange && markerId
                        ? `url(#${markerId})`
                        : undefined
                    }
                  />
                  {/* Transparent hit areas */}
                  {isFromDateInRange && (
                    <line
                      x1={`${positions.fromExitX}%`}
                      y1={entryY}
                      x2={`${positions.fromExitX}%`}
                      y2={barCenterY}
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ pointerEvents: 'stroke' }}
                    />
                  )}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                </>
              )}
            </g>
          )
        }
      }
      break
  }

  return elements
}

// ============================================================================
// Date Resolution
// ============================================================================

function resolveDependencyDates(
  dependency: Dependency,
  currentRowIndex: number,
  scheduledStart?: CalendarDateVO,
  scheduledEnd?: CalendarDateVO,
  today?: CalendarDateVO
): ResolvedDates {
  const { fromRowIndex, toRowIndex } = dependency

  // For the current row, use props (CalendarDateVO); for other rows, use embedded dates.
  // When dates are absent (unscheduled task), fall back to today's position
  // so that dependency lines connect to the placeholder bar.
  const fromEndDate: CalendarDateVO | null =
    currentRowIndex === fromRowIndex
      ? (scheduledEnd ?? today ?? null)
      : dependency.fromEndDate
        ? CalendarDateVO.of(dependency.fromEndDate)
        : (today ?? null)

  const toStartDate: CalendarDateVO | null =
    currentRowIndex === toRowIndex
      ? (scheduledStart ?? today ?? null)
      : dependency.toStartDate
        ? CalendarDateVO.of(dependency.toStartDate)
        : (today ?? null)

  return { fromEndDate, toStartDate }
}

// ============================================================================
// Position Calculation
// ============================================================================

interface DependencyPositions {
  fromX: number | null
  toX: number | null
  fromExitX: number | null
  toEntryX: number | null
}

// ピクセルベースのオフセット計算ユーティリティ
function pxToPercent(px: number, containerWidth: number): number {
  if (containerWidth <= 0) return 0
  return (px / containerWidth) * 100
}

// バー端と依存線の間の隙間（線のストロークや矢印マーカーがバーに食い込むのを防ぐ）
const BAR_EDGE_GAP_PX = 1.5

// 垂直線の折り返しオフセット（RightLabel marginLeft 12px 内に収める）
const EXIT_OFFSET_PX = 5

function calculatePositions(
  dates: ResolvedDates,
  datePositions: DatePosition[],
  containerWidth: number
): DependencyPositions {
  const gapPercent = pxToPercent(BAR_EDGE_GAP_PX, containerWidth)
  const exitPercent = pxToPercent(EXIT_OFFSET_PX, containerWidth)

  // 日付からパーセンテージ座標を取得
  const rawFromX = dates.fromEndDate
    ? getClampedPercentXForDate(datePositions, dates.fromEndDate, 'right')
    : null
  const rawToX = dates.toStartDate
    ? getClampedPercentXForDate(datePositions, dates.toStartDate, 'left')
    : null

  // 表示期間外にクランプされた座標にはバー端オフセットを適用しない
  // （期間外にはバーが存在せず、オフセットが端の隙間の原因になるため）
  const fromInRange = dates.fromEndDate
    ? getPercentXForDate(datePositions, dates.fromEndDate, 'right') !== null
    : false
  const toInRange = dates.toStartDate
    ? getPercentXForDate(datePositions, dates.toStartDate, 'left') !== null
    : false

  const fromGap = fromInRange ? gapPercent : 0
  const fromExit = fromInRange ? exitPercent : 0
  const toGap = toInRange ? gapPercent : 0
  const toExit = toInRange ? exitPercent : 0

  const fromX = rawFromX !== null ? rawFromX + fromGap : null
  const toX = rawToX !== null ? rawToX - toGap : null

  return {
    fromX,
    toX,
    fromExitX: rawFromX !== null ? rawFromX + fromGap + fromExit : null,
    toEntryX: rawToX !== null ? rawToX - toGap - toExit : null,
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

function getRowRole(
  currentRowIndex: number,
  fromRowIndex: number,
  toRowIndex: number
): RowRole {
  if (currentRowIndex === fromRowIndex) return 'source'
  if (currentRowIndex === toRowIndex) return 'target'
  return 'intermediate'
}

function isReversedDateOrder(dates: ResolvedDates): boolean {
  const { fromEndDate, toStartDate } = dates
  if (!fromEndDate || !toStartDate) return false
  return fromEndDate.isAfter(toStartDate) || fromEndDate.isEqual(toStartDate)
}
